import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { useFavorites } from "@/hooks/useFavorites";

const heroItems = [...movies].sort((a, b) => b.popularity - a.popularity).slice(0, 5);

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const { isFavorite, toggleFavorite } = useFavorites();

  const go = useCallback((next: number) => {
    setIndex((next + heroItems.length) % heroItems.length);
  }, []);

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % heroItems.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [paused]);

  const current = heroItems[index];
  const fav = isFavorite(current.id);

  return (
    <section
      className="relative w-full h-[440px] md:h-[600px] overflow-hidden bg-primary-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(e) => {
        touchStartX.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => {
        if (touchStartX.current === null) return;
        const delta = e.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(delta) > 50) go(delta < 0 ? index + 1 : index - 1);
        touchStartX.current = null;
      }}
    >
      {heroItems.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${i === index ? "opacity-100" : "opacity-0"}`}
          aria-hidden={i !== index}
        >
          <img
            src={item.backdrop}
            alt={`${item.title}剧照`}
            title={`${item.title} 高清影视`}
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/70 to-primary-950/20"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-primary-950/30"></div>
        </div>
      ))}

      <div className="relative z-10 h-full flex items-center">
        <div className="w-full px-6 md:px-12 lg:px-16">
          <div key={current.id} className="max-w-2xl animate-fade-up">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-2.5 py-1 rounded-full bg-accent-500 text-primary-950 text-xs font-heading font-bold">
                站内热播
              </span>
              <span className="px-2.5 py-1 rounded-full bg-background-50/15 text-background-50 text-xs font-label backdrop-blur">
                {current.type === "movie" ? "电影" : "剧集"} · {current.region}
              </span>
            </div>
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-background-50 leading-tight">
              {current.title}
            </h1>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mt-4 text-sm font-body text-background-200">
              <span className="flex items-center gap-1 text-accent-400 font-heading font-semibold">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-star-fill text-sm"></i>
                </span>
                {current.rating.toFixed(1)}
              </span>
              <span className="text-background-400">|</span>
              <span>{current.year}</span>
              <span className="text-background-400">|</span>
              <span>{current.director} 执导</span>
              <span className="text-background-400">|</span>
              <span>{current.genres.join(" / ")}</span>
            </div>
            <p className="mt-4 max-w-xl text-sm md:text-base font-body leading-relaxed text-background-200 line-clamp-3">
              {current.synopsis}
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-7">
              <Link
                to={`/detail/${current.id}`}
                className="flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-label font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-play-fill text-lg"></i>
                </span>
                立即观看
              </Link>
              <button
                type="button"
                onClick={() => toggleFavorite(current.id)}
                className={`flex items-center gap-2 px-5 py-3 rounded-md text-sm font-label font-semibold backdrop-blur transition-colors whitespace-nowrap cursor-pointer ${
                  fav ? "bg-accent-500 text-primary-950" : "bg-background-50/15 text-background-50 hover:bg-background-50/25"
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`${fav ? "ri-heart-3-fill" : "ri-heart-3-line"} text-lg`}></i>
                </span>
                {fav ? "已收藏" : "收藏"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute z-10 bottom-6 left-6 md:left-12 lg:left-16 flex items-center gap-3">
        <div className="flex items-center gap-2">
          {heroItems.map((item, i) => (
            <button
              key={item.id}
              type="button"
              onClick={() => go(i)}
              aria-label={`切换到${item.title}`}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                i === index ? "w-8 bg-accent-500" : "w-3 bg-background-50/50 hover:bg-background-50/80"
              }`}
            ></button>
          ))}
        </div>
      </div>

      <div className="absolute z-10 bottom-6 right-6 md:right-12 lg:right-16 hidden sm:flex items-center gap-2">
        <button
          type="button"
          onClick={() => go(index - 1)}
          aria-label="上一部"
          className="w-10 h-10 flex items-center justify-center rounded-full bg-background-50/15 text-background-50 backdrop-blur hover:bg-background-50/30 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-2xl"></i>
        </button>
        <button
          type="button"
          onClick={() => go(index + 1)}
          aria-label="下一部"
          className="w-10 h-10 flex items-center justify-center rounded-full bg-background-50/15 text-background-50 backdrop-blur hover:bg-background-50/30 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-2xl"></i>
        </button>
      </div>
    </section>
  );
}